import { delay, fromEvent, Subject, EMPTY } from 'rxjs';
import { tap, debounceTime, filter, map } from 'rxjs/operators';
import fastdom from 'fastdom';
import SearchBarResultCategory from 'enums/searchResultCategory';
import {
  filterSearchResultsByCategoryType,
  searchResultsCountsByCategoryType,
  getTabScrollButtonVisibility,
} from '../utils/searchBarResultHelper';
import SearchBarSuggestions from '../models/searchBarSuggestions';
import { createListNavigator } from '../utils/searchBarKeyboardHandler';

/**
 * Class representing the search bar result component.
 * Manages the display and interaction of search results.
 */
export default class SearchBarResult {
  constructor() {
    this.subscriptions = [];
    this.TAB_SCROLL_VISIBILITY_DELAY = 200;
    this.SCROLL_AMOUNT = 100;
    this.listNavigator = null;
    this.ALL_RESULTS_TAB_VALUE = SearchBarResultCategory.AllResults.id;
    this.selectedItem$ = new Subject();
  }

  /**
   * Initializes the search bar result component.
   * @param {Object} params - The parameters for initialization.
   * @param {HTMLElement} params.resultContainer - The container element for the search results.
   * @param {string} params.searchText - The current search text input by the user.
   * @param {Array} params.searchResults - The array of search results to display.
   */
  initialize({ resultContainer = null, searchText = '', searchResults = [] } = {}) {
    if (!resultContainer) {
      return;
    }

    // Initialize state
    this.resultContainer = resultContainer;
    this.searchText = searchText;
    this.searchResults = searchResults;
    this.allResults = searchResults;

    // Bind DOM elements
    this.bindDomElements();
    this.initializeActiveScreen();

    // Handle initial UI state
    this.updateNoResultsMessage();
    this.updateResultsVisibility();
    this.setupOutsideClickListener();
    this.activeSearchResultItemClass = 'nhs-n1-c-search-bar-results__item--active';
  }

  initializeActiveScreen() {
    const bigScreenContainer = this.resultContainer?.closest('[data-search-bar-container-desktop]');
    const smallScreenSearchContainer = this.resultContainer?.closest('[data-search-bar-container-mobile]');
    this.activeScreen = bigScreenContainer || smallScreenSearchContainer;
    this.isMobile = !!smallScreenSearchContainer;
  }

  /**
   * Binds frequently accessed DOM elements for later use.
   */
  bindDomElements() {
    const container = this.resultContainer;

    // Messages
    this.messageNoResultsMessage = container.querySelector('[data-search-results-message-container]');
    this.messageKeywordContainer = container.querySelector('[data-search-keyword-container]');
    this.messageKeyword = container.querySelector('[data-search-keyword]');

    // Total counts
    this.totalResultsCount = container.querySelector('[data-total-results-count]');
    this.totalLocationCount = container.querySelector('[data-total-location-count]');
    this.totalCommunitiesCount = container.querySelector('[data-total-communities-count]');
    this.totalBuildersCount = container.querySelector('[data-total-builders-count]');
    this.totalArticlesCount = container.querySelector('[data-total-articles-count]');

    // Tabs
    this.searchResultTabs = container.querySelectorAll('[data-search-result-tab]');
    this.tabLeftScrollButton = container.querySelector('[data-tabs-scroll-left-button]');
    this.tabRightScrollButton = container.querySelector('[data-tabs-scroll-right-button]');
    this.tabsContainer = container.querySelector('[data-tabs-container]');
  }

  /**
   * Updates the 'no results' message based on the current search results.
   */
  updateNoResultsMessage() {
    this.shouldShowNoResultsMessage(this.searchResults);
  }

  /**
   * Updates the visibility of the search results.
   */
  updateResultsVisibility() {
    if (this.searchResults.length > 0) {
      this.renderResults(this.searchResults, true);
      this.toggleResultsVisibility(true);
      this.scrollTabsToExtremeLeft();
    } else {
      this.toggleResultsVisibility(false);
    }
  }

  /**
   * Sets up a click listener on the document to hide search results when clicking outside.
   */
  setupOutsideClickListener() {
    this.setSearchResultsOutsideClickListener();
  }

  /**
   * Sets up a click listener on the document to hide search results when clicking outside.
   */
  setSearchResultsOutsideClickListener() {
    const click$ = fromEvent(document, 'click').pipe(tap((event) => this.handleDocumentClick(event)));

    const clickSubscription = click$.subscribe();
    this.subscriptions.push(clickSubscription);
  }

  /**
   * Handles document click events to hide search results when clicking outside.
   * @param {Event} event - The click event.
   */
  handleDocumentClick(event) {
    if (!this.resultContainer) {
      return;
    }

    const isClickInside = this.resultContainer.contains(event.target);
    if (!isClickInside) {
      this.hideSearchResults();
    }
  }

  /**
   * Hides the search results container.
   */
  hideSearchResults() {
    this.toggleResultsVisibility(false);
  }

  /**
   * Toggles the visibility of the search results container.
   * @param {boolean} show - Whether to show or hide the results container.
   */
  toggleResultsVisibility(show) {
    if (!this.resultContainer) {
      return;
    }

    this.setResultsHidden(!show);

    if (show) {
      this.initializeTabInteractions();
    }
  }

  /**
   * Sets the hidden property of the results container.
   * @param {boolean} hidden - Whether to hide the results container.
   */
  setResultsHidden(hidden) {
    this.resultContainer.hidden = hidden;
  }

  /**
   * Initializes tab interactions including scroll buttons and tab selection.
   */
  initializeTabInteractions() {
    this.setUpTabsScrollClickEvents();
    this.setUpTabSelection();
  }

  /**
   * Renders the search results and updates related UI elements.
   * @param {Array} searchResults - The array of search results to render.
   */
  renderResults(searchResults, isAllResults = false) {
    // Generate and render search suggestions
    this.renderSearchSuggestions(searchResults);

    const resultsToCount = isAllResults ? this.allResults : searchResults;
    // Update category counts
    this.updateCounts(this.allResults);

    // Show 'no results' message if needed
    this.shouldShowNoResultsMessage(resultsToCount);

    // Scroll to top
    this.scrollToTop();

    // Initialize keyboard navigation
    this.initializeKeyboardNavigation();
  }

  /**
   * Initializes keyboard navigation for the search results.
   */
  initializeKeyboardNavigation() {
    if (!this.resultContainer) {
      return;
    }

    this.destroyExistingNavigator();
    this.createNavigator();
  }

  /**
   * Destroys any existing list navigator instance.
   */
  destroyExistingNavigator() {
    if (this.listNavigator) {
      this.listNavigator.destroy();
      this.listNavigator = null;
    }
  }

  getInputElement() {
    return this.activeScreen?.querySelector('[data-search-input]') || null;
  }

  /**
   * Creates a new list navigator instance for keyboard navigation.
   */
  createNavigator() {
    this.listNavigator = createListNavigator(this.getInputElement(), this.resultContainer, {
      getItems: () => Array.from(this.resultContainer.querySelectorAll('[data-search-bar-result-item]')),
      onEnter: (item) => SearchBarResult.handleItemEnter(item, this),
      isMobile: this.isMobile,
      activeClass: this.activeSearchResultItemClass,
    });
  }

  /**
   * Handles the 'Enter' key event on a search result item.
   * @param {HTMLElement} item - The search result item that was activated.
   */
  static handleItemEnter(item, context) {
    const dataset = SearchBarResult.getItemDataset(item);
    SearchBarResult.applyItemEnterEffects(dataset, context);
  }

  /**
   * Extracts dataset information from the selected DOM item.
   * Pure function — no side effects.
   *
   * @param {HTMLElement} item - Selected DOM element.
   * @returns {Object|null} Dataset copy or null if invalid.
   */
  static getItemDataset(item) {
    if (!item?.dataset) {
      return null;
    }
    return { ...item.dataset };
  }

  /**
   * Applies all side effects related to item enter:
   * - Updates search text
   * - Triggers redirect or refresh action
   *
   * @param {Object|null} dataset - Dataset from getItemDataset.
   * @param {Object} context - Component context (with observables, etc.).
   */
  static applyItemEnterEffects(dataset, context) {
    if (!dataset) {
      return;
    }

    context.selectedItem$.next(dataset);
    context.hideSearchResults();
  }

  /**
   * Retrieves the observable for page refresh or redirect actions.
   * @param {Object} context - The context containing the observable.
   * @returns {Observable} The observable for page refresh or redirect actions.
   */
  static getSelectedItemObservable(context) {
    return context.selectedItem$ ?? EMPTY;
  }

  /**
   * Renders the search suggestions in the results container.
   * @param {Array} searchResults - The array of search results to render.
   */
  renderSearchSuggestions(searchResults) {
    const searchBarSuggestions = new SearchBarSuggestions(
      this.resultContainer,
      searchResults,
      this.searchText,
      this.isMobile,
    );
    searchBarSuggestions.render();
  }

  /**
   * Updates the counts of search results by category type and updates the UI.
   * @param {Array} searchResults - The array of search results to count.
   */
  updateCounts(searchResults) {
    const counts = searchResultsCountsByCategoryType(searchResults);
    this.updateTotalCounts(counts);
  }

  /**
   * Updates the visibility of the no results message based on the search results.
   * @param {Array} results - The array of search results.
   */
  shouldShowNoResultsMessage(results = []) {
    const shouldShow = this.searchText.length > 0 && results.length === 0;
    this.toggleMessageNoResults(shouldShow);
  }

  /**
   * Toggles the visibility of the no results message and updates the keyword.
   */
  toggleMessageNoResults(show) {
    if (!this.messageNoResultsMessage) {
      return;
    }

    this.setMessageVisibility(show);
    this.updateMessageKeyword();
  }

  /**
   * Sets the visibility of the no results message elements.
   * @param {boolean} show - Whether to show or hide the message elements.
   */
  setMessageVisibility(show) {
    this.messageNoResultsMessage.hidden = !show;
    this.messageKeywordContainer.hidden = !show;
  }

  /**
   * Updates the keyword displayed in the no results message.
   */
  updateMessageKeyword() {
    if (this.messageKeyword) {
      this.messageKeyword.textContent = this.searchText;
    }
  }

  /**
   * Updates the visibility of the tab scroll buttons based on the scroll position.
   * @param {HTMLElement} tabsContainer - The container element for the tabs.
   * @param {HTMLElement} leftButton - The left scroll button element.
   * @param {HTMLElement} rightButton - The right scroll button element.
   * @param {Object} fastdom - The fastdom object for DOM measurements.
   */
  static tabScrollButtonsVisibility(tabsContainer, leftButton, rightButton) {
    if (!tabsContainer || !leftButton || !rightButton) {
      return;
    }

    fastdom.measure(() => {
      const { scrollLeft, scrollWidth, clientWidth } = tabsContainer;

      const { leftHidden, rightHidden } = getTabScrollButtonVisibility(scrollLeft, scrollWidth, clientWidth);

      fastdom.mutate(() => {
        SearchBarResult.applyButtonsVisibility(leftButton, rightButton, !leftHidden, !rightHidden);
      });
    });
  }

  /**
   * Applies the visibility of the left and right scroll buttons.
   * @param {HTMLElement} leftButton - The left scroll button element.
   * @param {HTMLElement} rightButton - The right scroll button element.
   * @param {Object} visibility - The visibility state of the buttons.
   * @param {boolean} visibility.left - Whether to show the left button.
   * @param {boolean} visibility.right - Whether to show the right button.
   */
  static applyButtonsVisibility(leftButton, rightButton, left, right) {
    leftButton.hidden = !left;
    rightButton.hidden = !right;
  }

  /**
   * Sets up click events for the tab scroll buttons and manages their visibility.
   */
  setUpTabsScrollClickEvents() {
    if (!this.tabLeftScrollButton || !this.tabRightScrollButton || !this.tabsContainer) {
      return;
    }

    // Initialize scroll buttons visibility
    this.updateScrollButtonsVisibility();

    // Helper to handle scroll
    const handleTabScroll = (scrollLeft) => {
      fastdom.mutate(() => {
        this.tabsContainer.scrollBy({ left: scrollLeft, behavior: 'smooth' });
      });
    };

    // Helper to create scroll observable
    const createScrollClick$ = (button, scrollLeft) =>
      fromEvent(button, 'click').pipe(
        tap(() => handleTabScroll(scrollLeft)),
        delay(this.TAB_SCROLL_VISIBILITY_DELAY),
        tap(() => this.updateScrollButtonsVisibility()),
      );

    // Left and right scroll button observables
    const leftScroll$ = createScrollClick$(this.tabLeftScrollButton, -this.SCROLL_AMOUNT);
    const rightScroll$ = createScrollClick$(this.tabRightScrollButton, this.SCROLL_AMOUNT);

    // Subscribe and store for cleanup
    this.subscriptions.push(leftScroll$.subscribe(), rightScroll$.subscribe());

    // Update visibility on manual scroll
    const scrollSub = fromEvent(this.tabsContainer, 'scroll')
      .pipe(
        debounceTime(this.TAB_SCROLL_VISIBILITY_DELAY),
        tap(() => this.updateScrollButtonsVisibility()),
      )
      .subscribe();

    this.subscriptions.push(scrollSub);
  }

  /**
   * Scrolls the tabs container to the extreme left and selects the first tab.
   */
  scrollTabsToExtremeLeft() {
    if (!this.tabsContainer) {
      return;
    }

    // 1️ Scroll tabs to left
    this.scrollTabsLeft();

    // 2️ Update scroll buttons visibility after scrolling
    this.updateScrollButtonsVisibility();

    // 3️ Select the first tab
    this.selectFirstTab();
  }

  /**
   * Scrolls the tabs container to the leftmost position smoothly.
   */
  scrollTabsLeft() {
    fastdom.mutate(() => {
      this.tabsContainer.scrollTo({ left: 0, behavior: 'smooth' });
    });
  }

  /**
   * Updates the visibility of the scroll buttons based on the current scroll position.
   */
  updateScrollButtonsVisibility() {
    if (!this.tabLeftScrollButton || !this.tabRightScrollButton) {
      return;
    }

    SearchBarResult.tabScrollButtonsVisibility(this.tabsContainer, this.tabLeftScrollButton, this.tabRightScrollButton);
  }

  /**
   * Formats the counts for display, ensuring no undefined values.
   * @param {Object} counts - The counts object containing totals for each category.
   * @returns {Object} The formatted counts object with default '0' values.
   */
  static getDisplayCounts(counts = {}) {
    return {
      total: counts.total || '0',
      locations: counts.locations || '0',
      communities: counts.communities || '0',
      builders: counts.builders || '0',
      articles: counts.articles || '0',
    };
  }

  /**
   * Updates the total counts displayed in the UI.
   * @param {Object} counts - The counts object containing totals for each category.
   */
  updateTotalCounts(counts = {}) {
    const displayCounts = SearchBarResult.getDisplayCounts(counts);

    if (this.totalResultsCount) {
      this.totalResultsCount.textContent = displayCounts.total;
    }
    if (this.totalLocationCount) {
      this.totalLocationCount.textContent = displayCounts.locations;
    }
    if (this.totalCommunitiesCount) {
      this.totalCommunitiesCount.textContent = displayCounts.communities;
    }
    if (this.totalBuildersCount) {
      this.totalBuildersCount.textContent = displayCounts.builders;
    }
    if (this.totalArticlesCount) {
      this.totalArticlesCount.textContent = displayCounts.articles;
    }
  }

  /**
   * Sets up the tab selection event listeners.
   */
  setUpTabSelection() {
    if (!this.tabsContainer) {
      return;
    }

    const activeCssClass = 'nhs-n1-c-search-bar-tabs__tab--active';

    if (this.tabClickSubscription) {
      this.tabClickSubscription.unsubscribe();
    }

    this.tabClickSubscription = fromEvent(this.tabsContainer, 'click')
      .pipe(
        map((event) => event.target.closest('[data-search-result-tab]')),
        filter((tab) => !!tab && this.tabsContainer.contains(tab)),
        tap((tab) => this.handleTabClick(tab, activeCssClass)),
      )
      .subscribe();

    this.subscriptions.push(this.tabClickSubscription);
  }

  /**
   * Handles a tab click: updates UI and renders filtered results.
   * @param {HTMLElement} tab
   * @param {string} activeCssClass
   */
  handleTabClick(tab, activeCssClass) {
    this.activateTab(tab, activeCssClass);

    const tabValue = tab.getAttribute('data-search-result-tab');
    const filteredResults = this.getFilteredResults(tabValue);
    const isAllResults = this.isAllResultsTabSelected(tabValue);

    this.renderResults(filteredResults, isAllResults);
    this.toggleMessageNoResults(filteredResults.length === 0);
  }

  /**
   * Returns true when the passed value represents the "All results" tab.
   * Accepts numeric 1 or string '1'; everything else is false.
   * @param {*} value
   * @returns {boolean}
   */
  isAllResultsTabSelected(value) {
    return String(value) === this.ALL_RESULTS_TAB_VALUE;
  }

  /**
   * Adds active class to clicked tab and removes from others
   * @param {HTMLElement} tab
   * @param {string} activeCssClass
   */
  activateTab(tab, activeCssClass) {
    this.searchResultTabs.forEach((t) => {
      t.classList.remove(activeCssClass);
      t.setAttribute('aria-selected', 'false');
    });
    tab.classList.add(activeCssClass);
    tab.setAttribute('aria-selected', 'true');
  }

  /**
   * Returns filtered results by tab category
   * @param {string} tabValue
   * @returns {Array}
   */
  getFilteredResults(tabValue) {
    return filterSearchResultsByCategoryType(this.searchResults, tabValue);
  }

  /**
   * Gets the first tab element.
   * @returns {HTMLElement|null} The first tab element or null if not found.
   */
  getFirstTab() {
    return this.searchResultTabs?.[0] || null;
  }

  /**
   * Selects the first tab programmatically.
   */
  selectFirstTab() {
    const firstTab = this.getFirstTab();
    if (firstTab) {
      firstTab.click();
    }
  }

  /**
   * Scrolls the result container to the top smoothly.
   */
  scrollToTop() {
    const scrollableElement = this.resultContainer?.querySelector('[data-search-result-container]');
    if (scrollableElement) {
      fastdom.mutate(() => {
        scrollableElement.scrollTo({ top: 0, behavior: 'smooth' });
      });
    }
  }

  /** Cleans up subscriptions to prevent memory leaks. */
  destroy() {
    if (this.subscriptions) {
      this.subscriptions.forEach((subscription) => subscription.unsubscribe());
      this.subscriptions = [];
    }
  }
}
